// @skip-page-container-check
// Full 404 design: fixed full-screen dark + single centered glass modal. Same language as NotFoundPage.
import { useState, useEffect, type FormEvent } from "react"
import { Loader2, Mail, Lock, LogIn, ArrowRight, AlertCircle, Brackets } from "lucide-react"
import { useNavigate, Link as RouterLink } from "react-router-dom"
import { supabase } from "../lib/supabase"
import { useAuth } from "../components/AuthProvider"
import { navTargetToPath, ROUTES } from "../lib/routes"
import { useBodyScrollLock } from "../lib/useBodyScrollLock"
import { LusterShaderSlot } from "../components/LusterShaderSlot"
import { ActionSidebar } from "../components/ActionSidebar"
import { ToolGrid } from "../components/ToolGrid"
import { GlitchTerminal, type TerminalLine } from "../components/GlitchTerminal"

const DEMO_BYPASS_SECRET = import.meta.env.VITE_DEMO_BYPASS_SECRET as string | undefined
const DEMO_BYPASS_ACTUAL_PASSWORD = import.meta.env.VITE_DEMO_BYPASS_ACTUAL_PASSWORD as string | undefined


export default function LoginPage() {
  useBodyScrollLock()
  const navigate = useNavigate()
  const { user, loading, isSigningOut } = useAuth()

  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [formLoading, setFormLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [forgotMode, setForgotMode] = useState(false)
  const [resetEmail, setResetEmail] = useState("")
  const [resetLoading, setResetLoading] = useState(false)
  const [resetSent, setResetSent] = useState(false)
  const [resetError, setResetError] = useState<string | null>(null)
  const [authBurstTrigger, setAuthBurstTrigger] = useState(0)

  const loginTerminalLines: TerminalLine[] = [
    { prefix: "CONNECT", value: "luster.company:443", prefixColor: "luster", glitch: false },
    { prefix: "STATUS", value: "auth_required", prefixColor: "luster", glitch: true, valueColorBase: "amber" },
    { prefix: "SESSION", value: "undefined", prefixColor: "luster", glitch: true, valueColorBase: "red" },
    { prefix: "TOKEN", value: "null", prefixColor: "luster", glitch: true, valueColorBase: "red" },
    { prefix: "ACCESS", value: "pending", prefixColor: "luster", glitch: true, valueColorBase: "luster" },
  ]

  useEffect(() => {
    if (!loading && user) {
      navigate(navTargetToPath("home"), { replace: true })
    }
  }, [user, loading, navigate])

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setFormLoading(true)
    try {
      const passwordToUse =
        DEMO_BYPASS_SECRET &&
        DEMO_BYPASS_ACTUAL_PASSWORD &&
        password.trim() === DEMO_BYPASS_SECRET
          ? DEMO_BYPASS_ACTUAL_PASSWORD
          : password
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password: passwordToUse,
      })
      if (error) throw error
      setAuthBurstTrigger(n => n + 1)
      setTimeout(() => navigate(navTargetToPath("home"), { replace: true }), 600)
      return
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Authentication failed")
    } finally {
      setFormLoading(false)
    }
  }

  const handleForgot = async (e: FormEvent) => {
    e.preventDefault()
    setResetError(null)
    setResetLoading(true)
    try {
      const redirectTo = `${window.location.origin}/reset-password`
      const { error } = await supabase.auth.resetPasswordForEmail(resetEmail.trim(), { redirectTo })
      if (error) throw error
      setResetSent(true)
    } catch (err: unknown) {
      setResetError(err instanceof Error ? err.message : "Failed to send reset email.")
    } finally {
      setResetLoading(false)
    }
  }

  if (loading) return null

  return (
    <div className="fixed inset-0 z-50 bg-luster-950">
      <div className={`absolute inset-0 z-0 transition-opacity duration-300 ${isSigningOut ? "opacity-0" : "opacity-90"}`}>
        <LusterShaderSlot fill scale={1.2} fps={30} />
      </div>
      <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(ellipse_at_center,transparent_60%,rgba(0,0,0,0.15)_100%)]" />

      <div className="relative z-10 flex items-center justify-center min-h-full px-4 sm:px-10 lg:px-16 py-4 sm:py-10 lg:py-12">
        <div className="relative w-full max-w-xl max-h-[calc(100dvh-2rem)] sm:max-h-[calc(100dvh-5rem)] lg:max-h-[calc(100dvh-6rem)] rounded-3xl bg-black/40 backdrop-blur-2xl border border-white/[0.08] shadow-[0_32px_80px_-16px_rgba(0,0,0,0.6)] flex flex-col animate-in fade-in zoom-in-95 duration-500">
          <div className="shrink-0 flex items-center justify-between gap-4 px-8 sm:px-12 lg:px-14 pt-8 sm:pt-10 lg:pt-10 pb-4 border-b border-white/[0.06]">
            <div className="flex items-center gap-3">
              <img src="/brand/macOS/app-icon-256.png" alt="Luster" className="w-8 h-8 rounded-xl object-cover shrink-0" />
              <span className="font-mono text-[10px] text-white/20 tracking-[0.2em] uppercase">Portal sign in</span>
            </div>
            <ActionSidebar />
          </div>
          <div className="overflow-y-auto overscroll-contain min-h-0 p-8 sm:p-12 lg:p-14 pt-6 sm:pt-8 lg:pt-8 space-y-8">

            {/* Form block — 404-style headings + glass inputs */}
            <div className="space-y-6">
              {forgotMode ? (
                <div className="space-y-5">
                  <div>
                    <h2 className="text-xl sm:text-2xl font-bold text-white tracking-tight">Reset password</h2>
                    <p className="text-white/50 text-sm mt-1">Enter your email and we&apos;ll send a reset link.</p>
                  </div>
                  {resetSent ? (
                    <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-400/20 flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
                      <p className="text-sm text-emerald-200 font-medium">Check your email for a reset link.</p>
                    </div>
                  ) : (
                    <form onSubmit={handleForgot} className="space-y-4">
                      {resetError && (
                        <div className="p-4 rounded-xl bg-red-500/10 border border-red-400/20 flex items-start gap-3">
                          <AlertCircle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
                          <p className="text-sm text-red-200 font-medium">{resetError}</p>
                        </div>
                      )}
                      <div className="space-y-2">
                        <label className="text-[11px] font-bold uppercase tracking-widest text-white/50">Email</label>
                        <div className="relative">
                          <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40 pointer-events-none" />
                          <input
                            type="email"
                            required
                            autoFocus
                            value={resetEmail}
                            onChange={e => setResetEmail(e.target.value)}
                            className="w-full h-12 pl-12 pr-4 rounded-xl border border-white/10 bg-white/5 text-white placeholder:text-white/30 focus:ring-2 focus:ring-luster-400/30 focus:border-luster-400/40 outline-none transition-all text-sm"
                            placeholder="name@company.com"
                          />
                        </div>
                      </div>
                      <button
                        type="submit"
                        disabled={resetLoading}
                        className="w-full h-12 rounded-xl bg-white/10 border border-white/10 text-white font-bold text-[11px] uppercase tracking-widest hover:bg-white/15 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                      >
                        {resetLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send reset link"}
                      </button>
                    </form>
                  )}
                  <button
                    type="button"
                    onClick={() => { setForgotMode(false); setResetSent(false); setResetError(null) }}
                    className="text-xs font-bold text-white/50 hover:text-white/80 transition-colors"
                  >
                    ← Back to login
                  </button>
                </div>
              ) : (
                <>
                  <div>
                    <h2 className="text-xl sm:text-2xl font-bold text-white tracking-tight">Welcome back.</h2>
                    <p className="text-white/50 text-sm mt-1">Sign in to access the portal.</p>
                  </div>

                  {error && (
                    <div className="p-4 rounded-xl bg-red-500/10 border border-red-400/20 flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
                      <p className="text-sm text-red-200 font-medium">{error}</p>
                    </div>
                  )}

                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="space-y-2">
                      <label className="text-[11px] font-bold uppercase tracking-widest text-white/50">Email</label>
                      <div className="relative">
                        <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40 pointer-events-none" />
                        <input
                          type="email"
                          required
                          value={email}
                          onChange={e => setEmail(e.target.value)}
                          className="w-full h-12 pl-12 pr-4 rounded-xl border border-white/10 bg-white/5 text-white placeholder:text-white/30 focus:ring-2 focus:ring-luster-400/30 focus:border-luster-400/40 outline-none transition-all text-sm"
                          placeholder="name@company.com"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <label className="text-[11px] font-bold uppercase tracking-widest text-white/50">Password</label>
                        <button
                          type="button"
                          onClick={() => { setForgotMode(true); setResetEmail(email); setResetSent(false); setResetError(null) }}
                          className="text-[11px] font-bold text-luster-400 hover:text-luster-300 transition-colors py-1 min-h-[44px] min-w-[44px] flex items-center justify-end"
                        >
                          Forgot?
                        </button>
                      </div>
                      <div className="relative">
                        <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40 pointer-events-none" />
                        <input
                          type="password"
                          required
                          autoComplete="current-password"
                          value={password}
                          onChange={e => setPassword(e.target.value)}
                          className="w-full h-12 pl-12 pr-4 rounded-xl border border-white/10 bg-white/5 text-white placeholder:text-white/30 focus:ring-2 focus:ring-luster-400/30 focus:border-luster-400/40 outline-none transition-all text-sm"
                          placeholder="••••••••"
                        />
                      </div>
                    </div>
                    <button
                      type="submit"
                      disabled={formLoading}
                      className="w-full h-14 rounded-2xl bg-amber-400/10 border border-amber-400/20 text-amber-200 hover:bg-teal-400/10 hover:border-teal-400/20 hover:text-teal-200 shadow-lg shadow-black/20 flex items-center justify-center gap-2.5 transition-colors duration-300 active:scale-[0.98]"
                    >
                      {formLoading ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <>
                          <span className="uppercase tracking-[0.15em] text-[11px] font-black">Access portal</span>
                          <ArrowRight className="w-3.5 h-3.5 opacity-80" />
                        </>
                      )}
                    </button>
                  </form>

                  <p className="text-center text-sm text-white/50">
                    New to Luster?{" "}
                    <RouterLink to={ROUTES.apply} className="font-semibold text-teal-400 hover:text-teal-300 transition-colors">
                      Start free →
                    </RouterLink>
                  </p>
                </>
              )}
            </div>

            <div className="border-t border-white/[0.06] pt-6">
              <ToolGrid />
            </div>

            {/* Terminal readout — decorative, below the fold */}
            <div className="border-t border-white/[0.06] pt-6">
              <GlitchTerminal
                lines={loginTerminalLines}
                intensity={3.5}
                externalTrigger={authBurstTrigger}
                duration={400}
              />
            </div>

          </div>
        </div>
      </div>
    </div>
  )
}
